import { useCurrentCart } from 'connectors/cart/Cart';
import { CartFragmentApi, useActionEmptyCartMutationApi } from 'graphql/generated';
import { useLatest } from 'hooks/ui/useLatest';
import { dispatchBroadcastChannel } from 'hooks/useBroadcastChannel';
import { useDomainConfig } from 'hooks/useDomainConfig';
import { useCallback } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { GtmListNameType } from 'types/gtm';
import { onRemoveCartItemGtmEventHandler } from 'utils/Gtm/EventHandlers';
import { useGtmCartEventInfo } from 'utils/Gtm/Gtm';

export type ActionEmptyCartHandler = (gtmListName: GtmListNameType) => Promise<CartFragmentApi | undefined | null>;

export const useActionEmptyCart = (): [ActionEmptyCartHandler, boolean] => {
    const [{ fetching }, actionEmptyCart] = useActionEmptyCartMutationApi();
    const { cartUuid, defaultStoreUuid } = usePersistStore((s) => s);
    const { cart } = useCurrentCart();
    const { currencyCode, url } = useDomainConfig();
    const gtmCartEventInfo = useGtmCartEventInfo();
    const gtmCart = useLatest(gtmCartEventInfo.cart);
    const { updateCartUuidState } = usePersistStore((s) => s);

    const actionEmptyCartHandler = useCallback<ActionEmptyCartHandler>(
        async (gtmListName: GtmListNameType) => {
            const oldCartItems = cart?.items ?? [];

            const actionEmptyCartResult = await actionEmptyCart({
                input: { cartUuid },
                defaultStoreUuid,
                selectedStoreUuid: null,
            });

            if (actionEmptyCartResult.data?.ActionEmptyCart.uuid !== undefined) {
                updateCartUuidState(actionEmptyCartResult.data.ActionEmptyCart.uuid);

                oldCartItems.forEach((cartItem, index) => {
                    const absoluteEventValue = cartItem.product.price.priceWithoutVat * cartItem.quantity;
                    const absoluteEventValueWithTax = cartItem.product.price.priceWithVat * cartItem.quantity;

                    setTimeout(() => {
                        onRemoveCartItemGtmEventHandler(
                            gtmCart.current,
                            cartItem,
                            currencyCode,
                            absoluteEventValue,
                            absoluteEventValueWithTax,
                            index,
                            gtmListName,
                            url,
                        );
                    });
                }, 0);

                dispatchBroadcastChannel({ name: 'refetchCart' });
            }

            return actionEmptyCartResult.data?.ActionEmptyCart;
        },
        [cartUuid, cart, defaultStoreUuid, updateCartUuidState, actionEmptyCart, gtmCart, currencyCode, url],
    );

    return [actionEmptyCartHandler, fetching];
};
